import React, { useState, useEffect } from 'react';
import { Layout, Menu, Card, Typography, Button, Space, Alert, Spin, Row, Col, Drawer, message } from 'antd';
import {
  DashboardOutlined,
  HistoryOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  LogoutOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  BookOutlined,
  MedicineBoxOutlined,
  SettingOutlined,
  ExperimentOutlined,
  MenuOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { exportMedicalRecordPDF, downloadPDF } from '../services/pdfExportService';
import { usePatientPortal } from '../context/PatientPortalContext';
import { useNavigate } from 'react-router-dom';
import PatientEducationPortal from '../components/PatientEducationPortal';
import NewPatientPortalHealthOverview from '../components/NewPatientPortalHealthOverview';
import ModernPatientHealthOverview from '../components/ModernPatientHealthOverview';
import PatientPortalMedicalHistoryPage from './PatientPortalMedicalHistoryPage';
import PatientPortalTimeline from '../components/PatientPortalTimeline';
import PatientPortalDiagnosticsPage from '../components/PatientPortalDiagnosticsPage';
import PatientPortalMedications from '../components/PatientPortalMedications';
import PatientPortalSettings from './PatientPortalSettings';
import SystemMediatedSimulationSandbox from '../components/SystemMediatedSimulationSandbox';
import PatientPortalSurveyLinks from '../components/PatientPortalSurveyLinks';

const { Header, Content } = Layout;
const { Title, Text, Paragraph } = Typography;

const PatientPortalDashboard = () => {
  const navigate = useNavigate();
  const {
    patient,
    logout,
    isAuthenticated,
    portalAccess,
    isAccessExpired,
    getDaysUntilExpiration
  } = usePatientPortal();

  const [selectedKey, setSelectedKey] = useState('overview');
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Responsive: detect mobile
  const isMobile = window.innerWidth < 768;

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('Patient portal: Not authenticated, redirecting to login');
      navigate('/portal/login');
    }
  }, [isAuthenticated, navigate]);

  // Additional check for token validity
  useEffect(() => {
    const checkTokenValidity = () => {
      const portalToken = localStorage.getItem('portalAuthToken');
      if (!portalToken && isAuthenticated) {
        console.log('Patient portal: Token missing but context shows authenticated, logging out');
        logout();
        navigate('/portal/login');
      }
    };

    // Check token validity on mount and when authentication state changes
    checkTokenValidity();
  }, [isAuthenticated, logout, navigate]);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  const handleExportPDF = async () => {
    if (!patient?._id) {
      message.error('Không tìm thấy thông tin bệnh nhân');
      return;
    }

    try {
      message.loading({ content: 'Đang tạo hồ sơ y khoa PDF...', key: 'pdf-export', duration: 0 });

      const { blob, filename } = await exportMedicalRecordPDF(patient._id);

      downloadPDF(blob, filename);

      message.success({ content: 'Xuất hồ sơ y khoa thành công!', key: 'pdf-export', duration: 3 });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      message.error({
        content: `Không thể xuất hồ sơ: ${error.response?.data?.message || error.message}`,
        key: 'pdf-export',
        duration: 5
      });
    }
  };

  const menuItems = [
    {
      key: 'overview',
      icon: <DashboardOutlined />,
      label: 'Health Overview',
      onClick: () => {
        setSelectedKey('overview');
        setDrawerVisible(false);
      }
    },
    {
      key: 'medical-history',
      icon: <FileTextOutlined />,
      label: 'Medical History',
      onClick: () => {
        setSelectedKey('medical-history');
        setDrawerVisible(false);
      }
    },
    {
      key: 'timeline',
      icon: <ClockCircleOutlined />,
      label: 'Health Timeline',
      onClick: () => {
        setSelectedKey('timeline');
        setDrawerVisible(false);
      }
    },
    {
      key: 'diagnostics',
      icon: <ExperimentOutlined />,
      label: 'Diagnostics',
      onClick: () => {
        setSelectedKey('diagnostics');
        setDrawerVisible(false);
      }
    },
    {
      key: 'medications',
      icon: <MedicineBoxOutlined />,
      label: 'Medications',
      onClick: () => {
        setSelectedKey('medications');
        setDrawerVisible(false);
      }
    },
    {
      key: 'education',
      icon: <BookOutlined />,
      label: 'Patient Education',
      onClick: () => {
        setSelectedKey('education');
        setDrawerVisible(false);
      }
    },
    {
      key: 'surveys',
      icon: <FileTextOutlined />,
      label: 'Health Surveys',
      onClick: () => {
        setSelectedKey('surveys');
        setDrawerVisible(false);
      }
    },
    {
      key: 'export-pdf',
      icon: <FilePdfOutlined />,
      label: 'Xuất hồ sơ y khoa',
      onClick: () => {
        handleExportPDF();
        setDrawerVisible(false);
      }
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => {
        setSelectedKey('settings');
        setDrawerVisible(false);
      }
    }
  ];

  const daysUntilExpiration = getDaysUntilExpiration();
  const showExpirationWarning = daysUntilExpiration !== null && daysUntilExpiration <= 7;

  const renderContent = () => {
    switch (selectedKey) {
      case 'overview':
        return <ModernPatientHealthOverview />;

      case 'medical-history':
        return <PatientPortalMedicalHistoryPage />;

      case 'timeline':
        return <PatientPortalTimeline />;

      case 'diagnostics':
        return <PatientPortalDiagnosticsPage />;

      case 'medications':
        return <PatientPortalMedications />;

      case 'education':
        return <PatientEducationPortal />;

      case 'surveys':
        return <PatientPortalSurveyLinks />;

      case 'settings':
        return <PatientPortalSettings />;

      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Main Header with Horizontal Navigation */}
      <Header
        style={{
          background: '#fff',
          padding: isMobile ? '0 12px' : '0 24px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          position: 'fixed',
          width: '100%',
          top: 0,
          zIndex: 1000,
          height: isMobile ? '56px' : '64px'
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: 1,
          minWidth: 0
        }}>
          <img
            src="/icons/care360-logo.png"
            alt="Care360"
            style={{
              height: isMobile ? '32px' : '40px',
              marginRight: isMobile ? 16 : 32,
              objectFit: 'contain'
            }}
          />

          {/* Mobile: show menu button */}
          {isMobile ? (
            <Button
              type="text"
              icon={<MenuOutlined style={{ fontSize: 24 }} />}
              onClick={() => setDrawerVisible(true)}
              style={{ marginLeft: 8 }}
            />
          ) : (
            <Menu
              mode="horizontal"
              selectedKeys={[selectedKey]}
              style={{
                border: 'none',
                background: 'transparent',
                flex: 1,
                minWidth: 0
              }}
              items={menuItems}
            />
          )}
        </div>

        <Space size={isMobile ? 8 : "middle"}>
          <Text>
            <UserOutlined /> {patient?.name}
          </Text>
          <Button 
            type="text" 
            icon={<LogoutOutlined />} 
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Space>
      </Header>

      {/* Drawer for mobile menu */}
      <Drawer
        title="Patient Portal Menu"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ padding: 0 }}
      >
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          style={{ border: 'none' }}
          items={menuItems}
        />
      </Drawer>

      {/* Main Content */}
      <Content style={{ 
        background: '#f5f5f5', 
        padding: '0', 
        marginTop: isMobile ? '56px' : '64px',
        minHeight: `calc(100vh - ${isMobile ? '56px' : '64px'})`
      }}>
        {showExpirationWarning && (
          <Alert
            message="Portal Access Expiring Soon"
            description={`Your portal access will expire in ${daysUntilExpiration} day${daysUntilExpiration !== 1 ? 's' : ''}. Please contact your healthcare provider to renew your access.`}
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ marginBottom: '24px' }}
            closable
          />
        )}

        {isAccessExpired() && (
          <Alert
            message="Portal Access Expired"
            description="Your portal access has expired. Please contact your healthcare provider for a new access link."
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {renderContent()}
      </Content>
    </Layout>
  );
};

export default PatientPortalDashboard;
